import { StatusCodes } from 'http-status-codes';
import { getErrorMessage, getErrorStatus } from '@common/utils';

/**
 * TELV1-606 — the analyzer answers a filter_id that is unknown, deleted or from
 * another project with 404 + this message. KEEP IN SYNC with the backend's
 * `ErrorMessage.FILTER_NOT_FOUND` (libs/common/src/types/error.code.ts): if the
 * text drifts, stale-filter recovery silently stops matching.
 * It is an expected user state (an old bookmark, another tab, a filter restored
 * after a re-run), not a bug: callers recover instead of reporting it.
 */
export const FILTER_NOT_FOUND_MESSAGE = 'Filter not found';

export const isFilterNotFoundError = (error: unknown): boolean =>
  getErrorStatus(error) === StatusCodes.NOT_FOUND &&
  getErrorMessage(error) === FILTER_NOT_FOUND_MESSAGE;

/**
 * Variant for file downloads: exports request `responseType: 'blob'`, so the
 * JSON error body arrives as a Blob and must be read before it can be checked.
 */
export const isFilterNotFoundErrorAsync = async (error: unknown): Promise<boolean> => {
  if (isFilterNotFoundError(error)) return true;
  if (getErrorStatus(error) !== StatusCodes.NOT_FOUND) return false;

  const shape = error as { data?: unknown; response?: { data?: unknown } } | null;
  const body = shape?.data ?? shape?.response?.data;
  if (!(body instanceof Blob)) return false;

  try {
    const parsed = JSON.parse(await body.text()) as { message?: unknown };
    return parsed?.message === FILTER_NOT_FOUND_MESSAGE;
  } catch {
    return false;
  }
};
